/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { cssMap, cx, jsx } from '@atlaskit/css';
import type { TriggerProps } from '@atlaskit/popup/types';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import Suspense from '@atlaskit/react-ufo/suspense';
import { token } from '@atlaskit/tokens';

import { ShimmerText } from '../../compact-research-steps/ShimmerText';
import { getIconColor } from '../skill-colors';
import type { SkillIconComponent } from '../skill-icons';
import type { RovoSkillTagProps } from './SkillTag';

const styles = cssMap({
	parallelogram: {
		marginLeft: token('space.050'),
		paddingLeft: token('space.075'),
		paddingRight: token('space.075'),
		borderRadius: token('radius.xsmall'),
	},
	background: {
		backgroundColor: token('color.background.neutral'),
	},
	backgroundInverse: {
		backgroundColor: token('color.background.inverse.subtle'),
	},
	parallelogramWithWrapping: {
		boxSizing: 'border-box',
		boxDecorationBreak: 'clone',
		display: 'inline',
		transform: 'none',
	},
	parallelogramWithSlantedWrapping: {
		display: 'inline',
		marginLeft: token('space.0'),
		paddingLeft: token('space.0'),
		paddingRight: token('space.0'),
		transform: 'none',
	},
	contentWithWrapping: {
		display: 'inline',
		transform: 'none',
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-all',
	},
	contentWithSlantedWrapping: {
		display: 'inline',
		transform: 'none',
		whiteSpace: 'normal',
		overflowWrap: 'anywhere',
	},
	labelWithWrapping: {
		display: 'inline',
	},
	highlightWithWrapping: {
		boxDecorationBreak: 'clone',
		display: 'inline',
	},
	highlightStart: {
		display: 'inline',
		paddingLeft: token('space.050'),
		clipPath: 'polygon(20% 0, 100% 0, 100% 100%, 0 100%)',
	},
	highlightEnd: {
		display: 'inline',
		paddingRight: token('space.050'),
		clipPath: 'polygon(0 0, 100% 0, 80% 100%, 0 100%)',
	},
	slashWithWrapping: {
		display: 'inline',
		marginRight: token('space.050'),
	},
	slashWithSlantedWrapping: {
		display: 'inline',
		paddingRight: token('space.050'),
	},
	iconWithWrapping: {
		display: 'inline-flex',
		marginRight: token('space.050'),
		transform: 'translateY(-1px)',
		verticalAlign: 'middle',
	},
});

/**
 * Pure presentational rendering of a skill tag lozenge. Kept separate from `SkillTag` so it can
 * also be rendered by `SkillHoverCardContent` without a runtime import cycle (`SkillTag` already
 * imports `SkillHoverCardContent` for its own hovercard).
 */
export const SkillTagInternal = ({
	triggerProps = {},
	invertColors,
	slug,
	IconComponent,
	onMouseEnter,
	onMouseLeave,
	color,
	shimmer,
	shouldUseSlantedWrapping,
}: {
	triggerProps?: Partial<TriggerProps>;
	onMouseEnter?: React.MouseEventHandler;
	onMouseLeave?: React.MouseEventHandler;
	IconComponent?: SkillIconComponent;
	shouldUseSlantedWrapping: boolean;
} & Omit<RovoSkillTagProps, 'iconName'>): JSX.Element => {
	const iconColor = getIconColor(color, invertColors);
	const slugBeforeLastCharacter = slug.slice(0, -1);
	const lastSlugCharacter = slug.slice(-1);
	const wrappingLabel = (
		<>
			<Box
				as="span"
				xcss={cx(
					styles.highlightStart,
					styles.slashWithSlantedWrapping,
					invertColors ? styles.backgroundInverse : styles.background,
				)}
				testId="skill-tag-highlight-start"
				/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Slash color is derived at runtime from skill metadata. */
				style={{ color: iconColor }}
			>
				/
			</Box>
			{(IconComponent || slugBeforeLastCharacter) && (
				<Box
					as="span"
					xcss={cx(
						styles.highlightWithWrapping,
						invertColors ? styles.backgroundInverse : styles.background,
					)}
				>
					{IconComponent && (
						<Box as="span" xcss={styles.iconWithWrapping}>
							<Suspense fallback={null} interactionName="skill-tag-icon">
								<IconComponent label="" size="small" color={iconColor} />
							</Suspense>
						</Box>
					)}
					{slugBeforeLastCharacter}
				</Box>
			)}
			{lastSlugCharacter && (
				<Box
					as="span"
					xcss={cx(
						styles.highlightEnd,
						invertColors ? styles.backgroundInverse : styles.background,
					)}
					testId="skill-tag-highlight-end"
				>
					{lastSlugCharacter}
				</Box>
			)}
		</>
	);

	return (
		<Box
			as="span"
			data-inline-extension-layout="flow"
			xcss={cx(
				styles.parallelogram,
				!shouldUseSlantedWrapping && styles.parallelogramWithWrapping,
				shouldUseSlantedWrapping && styles.parallelogramWithSlantedWrapping,
				!shouldUseSlantedWrapping && (invertColors ? styles.backgroundInverse : styles.background),
			)}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			{...triggerProps}
		>
			{shouldUseSlantedWrapping ? (
				<Box as="span" xcss={styles.contentWithSlantedWrapping}>
					{shimmer ? (
						<ShimmerText as="span">{wrappingLabel}</ShimmerText>
					) : (
						<Text color={invertColors ? 'color.text.inverse' : 'color.text'}>{wrappingLabel}</Text>
					)}
				</Box>
			) : (
				<Box as="span" xcss={styles.contentWithWrapping}>
					<Box
						as="span"
						xcss={styles.slashWithWrapping}
						/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Slash color is derived at runtime from skill metadata. */
						style={{ color: iconColor }}
					>
						/
					</Box>
					{IconComponent && (
						<Box as="span" xcss={styles.iconWithWrapping}>
							<Suspense fallback={null} interactionName="skill-tag-icon">
								<IconComponent label="" size="small" color={iconColor} />
							</Suspense>
						</Box>
					)}
					<Box as="span" xcss={styles.labelWithWrapping}>
						{shimmer ? (
							<ShimmerText as="span">{slug}</ShimmerText>
						) : (
							<Text color={invertColors ? 'color.text.inverse' : 'color.text'}>{slug}</Text>
						)}
					</Box>
				</Box>
			)}
		</Box>
	);
};
